/**
 * Strategy Builder Page — "Strategy Workbench"
 *
 * Build + Test in one place.
 * - Top bar: strategy name, metadata, Import/Export/Reset
 * - Middle: TreeCanvas (left) + right panel (Properties | DSL Code | Backtest)
 * - Bottom: Backtest results (MetricsGrid, EquityChart, Allocations) — appears after running
 *
 * Theme-aware via surface, primary, accent CSS properties.
 */

import { useState, useRef, useEffect, useCallback, type PointerEvent as ReactPointerEvent } from 'react';
import clsx from 'clsx';
import { useBuilderStore, useStrategy } from '../stores/builderStore';
import type { BuilderSourceType } from '../stores/builderStore';
import { TreeCanvas, PropertiesPanel, CodePreview, BacktestPanel, BacktestResults } from '../components/builder';
import { generateDSL } from '../services/dslService';
import { createStrategy, updateStrategy, bookmarkStrategy, invalidateASTCache } from '../services/strategyService';
import { useAuthStore } from '../stores/authStore';
import { isAdmin } from '../services/authService';
import type { BacktestResult } from '../stores/backtestStore';

type RightTab = 'properties' | 'code' | 'backtest';
type MobileView = 'tree' | 'panel';

export default function StrategyBuilderPage() {
  const strategy = useStrategy();
  const updateMetadata = useBuilderStore((s) => s.updateMetadata);
  const reset = useBuilderStore((s) => s.reset);
  const importStrategy = useBuilderStore((s) => s.importStrategy);
  const source = useBuilderStore((s) => s.source);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const hideDsl = source.type === 'community' && !isAdmin(user);

  const [rightTab, setRightTab] = useState<RightTab>('properties');
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(strategy.metadata.name);
  const nameRef = useRef<HTMLInputElement>(null);
  const [mobileView, setMobileView] = useState<MobileView>('tree');
  const [showMobileActions, setShowMobileActions] = useState(false);

  // Save/Bookmark state
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Backtest state
  const [backtestResult, setBacktestResult] = useState<BacktestResult | null>(null);
  const [backtestError, setBacktestError] = useState('');
  const [isBacktesting, setIsBacktesting] = useState(false);

  // Resizable right panel
  const [rightPanelWidth, setRightPanelWidth] = useState(320);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(320);

  const handleResizePointerDown = useCallback((e: ReactPointerEvent) => {
    e.preventDefault();
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragStartWidth.current = rightPanelWidth;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [rightPanelWidth]);

  const handleResizePointerMove = useCallback((e: ReactPointerEvent) => {
    if (!isDragging.current) return;
    const delta = dragStartX.current - e.clientX; // dragging left = wider panel
    const newWidth = Math.min(800, Math.max(200, dragStartWidth.current + delta));
    setRightPanelWidth(newWidth);
  }, []);

  const handleResizePointerUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  useEffect(() => {
    setNameValue(strategy.metadata.name);
  }, [strategy.metadata.name]);

  useEffect(() => {
    if (isEditingName && nameRef.current) {
      nameRef.current.focus();
      nameRef.current.select();
    }
  }, [isEditingName]);

  const handleNameSubmit = useCallback(() => {
    if (nameValue.trim()) updateMetadata({ name: nameValue.trim() });
    else setNameValue(strategy.metadata.name);
    setIsEditingName(false);
  }, [nameValue, strategy.metadata.name, updateMetadata]);

  const handleExportJSON = useCallback(() => {
    const data = JSON.stringify(strategy, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${strategy.metadata.name.toLowerCase().replace(/\s+/g, '-')}.tradecraft.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [strategy]);

  const handleExportDSL = useCallback(() => {
    const dsl = generateDSL(strategy);
    const blob = new Blob([dsl], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${strategy.metadata.name.toLowerCase().replace(/\s+/g, '-')}.tc`;
    a.click();
    URL.revokeObjectURL(url);
  }, [strategy]);

  const handleImport = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const text = await file.text();
      try {
        const parsed = JSON.parse(text);
        if (parsed.metadata && parsed.root) importStrategy(parsed);
      } catch {
        // Invalid file — silently ignore
      }
    };
    input.click();
  }, [importStrategy]);

  const handleBacktestResult = useCallback((result: BacktestResult) => {
    setBacktestResult(result);
    setBacktestError('');
  }, []);

  const handleBacktestError = useCallback((error: string) => {
    setBacktestError(error);
  }, []);

  // Clear save messages after 4 seconds
  useEffect(() => {
    if (saveSuccess || saveError) {
      const t = setTimeout(() => { setSaveSuccess(''); setSaveError(''); }, 4000);
      return () => clearTimeout(t);
    }
  }, [saveSuccess, saveError]);

  /**
   * Share to Community: only available for NEW strategies.
   * Creates a public community strategy.
   */
  const handleShareToCommunity = useCallback(async () => {
    if (!isAuthenticated) return;
    // Block default name — user must rename before sharing
    if (strategy.metadata.name.trim().toLowerCase() === 'new strategy') {
      setSaveError('Please give your strategy a name before sharing to community.');
      setIsEditingName(true);
      return;
    }
    // Require backtest before sharing — stats must be populated
    if (!backtestResult?.metrics) {
      setSaveError('Please run a backtest first so the strategy has performance stats.');
      setRightTab('backtest');
      return;
    }
    const dsl = generateDSL(strategy);
    setIsSaving(true);
    setSaveError('');
    try {
      const created = await createStrategy({
        name: strategy.metadata.name,
        code: dsl,
        description: strategy.metadata.description || '',
        visibility: 'public',
        is_community: true,
        metrics: backtestResult?.metrics ? {
          cagr: backtestResult.metrics.cagr,
          sharpe: backtestResult.metrics.sharpe,
          sortino: backtestResult.metrics.sortino,
          maxDrawdown: backtestResult.metrics.maxDrawdown,
          volatility: backtestResult.metrics.volatility,
          calmar: backtestResult.metrics.calmar,
          bestYear: backtestResult.metrics.bestYear,
          worstYear: backtestResult.metrics.worstYear,
          totalReturn: backtestResult.metrics.totalReturn,
          tradingDays: backtestResult.metrics.tradingDays,
          trailing1m: backtestResult.metrics.trailing1m,
          trailing3m: backtestResult.metrics.trailing3m,
          alpha: backtestResult.metrics.alpha,
          beta: backtestResult.metrics.beta,
          rSquared: backtestResult.metrics.rSquared,
          correlation: backtestResult.metrics.correlation,
        } : undefined,
      });
      // After sharing, source becomes the community strategy
      useBuilderStore.getState().loadFromDSL(
        dsl,
        'community' as BuilderSourceType,
        created.id,
        created.name,
      );
      setSaveSuccess(`"${created.name}" shared to community!`);
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Failed to share strategy');
    } finally {
      setIsSaving(false);
    }
  }, [isAuthenticated, strategy, backtestResult]);

  /**
   * Bookmark: creates a private copy and bookmarks it.
   * Available for NEW and COMMUNITY sources.
   */
  const handleBookmark = useCallback(async () => {
    if (!isAuthenticated) return;
    // Require backtest before bookmarking — stats must be populated
    if (!backtestResult?.metrics) {
      setSaveError('Please run a backtest first so the strategy has performance stats.');
      setRightTab('backtest');
      return;
    }
    const dsl = generateDSL(strategy);
    setIsSaving(true);
    setSaveError('');
    try {
      // Append suffix to avoid name collision with community original
      const suffix = source.type === 'community' ? ' (my copy)' : '';
      const created = await createStrategy({
        name: `${strategy.metadata.name}${suffix}`,
        code: dsl,
        description: strategy.metadata.description || '',
        visibility: 'private',
        metrics: backtestResult?.metrics ? {
          cagr: backtestResult.metrics.cagr,
          sharpe: backtestResult.metrics.sharpe,
          sortino: backtestResult.metrics.sortino,
          maxDrawdown: backtestResult.metrics.maxDrawdown,
          volatility: backtestResult.metrics.volatility,
          calmar: backtestResult.metrics.calmar,
          bestYear: backtestResult.metrics.bestYear,
          worstYear: backtestResult.metrics.worstYear,
          totalReturn: backtestResult.metrics.totalReturn,
          tradingDays: backtestResult.metrics.tradingDays,
          trailing1m: backtestResult.metrics.trailing1m,
          trailing3m: backtestResult.metrics.trailing3m,
          alpha: backtestResult.metrics.alpha,
          beta: backtestResult.metrics.beta,
          rSquared: backtestResult.metrics.rSquared,
          correlation: backtestResult.metrics.correlation,
        } : undefined,
      });
      await bookmarkStrategy(created.id);
      // After bookmarking, source becomes the user's own strategy
      useBuilderStore.getState().loadFromDSL(
        dsl,
        'user' as BuilderSourceType,
        created.id,
        created.name,
      );
      setSaveSuccess(`Strategy bookmarked as "${created.name}"`);
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Failed to bookmark strategy');
    } finally {
      setIsSaving(false);
    }
  }, [isAuthenticated, strategy, source, backtestResult]);

  /**
   * Update: overwrites user's own existing strategy.
   * Shows confirmation first, then updates + invalidates AST cache.
   */
  const handleUpdate = useCallback(() => {
    if (!isAuthenticated || !source.strategyId) return;
    setShowConfirmDialog(true);
  }, [isAuthenticated, source]);

  const handleConfirmUpdate = useCallback(async () => {
    setShowConfirmDialog(false);
    if (!source.strategyId) return;

    const dsl = generateDSL(strategy);
    setIsSaving(true);
    setSaveError('');
    try {
      await updateStrategy(source.strategyId, {
        name: strategy.metadata.name,
        code: dsl,
      });
      // Belt-and-suspenders: explicitly invalidate AST cache
      await invalidateASTCache(source.strategyId);
      setSaveSuccess('Strategy updated! Live trading will use the new code.');
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Failed to update strategy');
    } finally {
      setIsSaving(false);
    }
  }, [strategy, source]);

  return (
    <div className="h-full flex flex-col bg-surface-900">
      {/* ─── Top Bar ─────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between px-3 md:px-4 py-2 bg-surface-800/80 border-b border-surface-700 shrink-0 gap-y-1.5">
        {/* Left: Strategy name + metadata */}
        <div className="flex items-center gap-2 md:gap-3 min-w-0">
          {isEditingName ? (
            <input
              ref={nameRef}
              type="text"
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onBlur={handleNameSubmit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleNameSubmit();
                if (e.key === 'Escape') { setNameValue(strategy.metadata.name); setIsEditingName(false); }
              }}
              className="px-2 py-0.5 rounded-md text-sm font-semibold bg-surface-900 border border-primary-500/50 text-surface-100 focus:outline-none focus:ring-1 focus:ring-primary-500/40 w-full max-w-[200px] md:max-w-[250px]"
            />
          ) : (
            <button
              onClick={() => setIsEditingName(true)}
              className="text-sm font-semibold text-surface-100 hover:text-primary-400 transition-colors truncate max-w-[150px] md:max-w-[250px]"
              title="Click to rename"
            >
              {strategy.metadata.name}
            </button>
          )}

          {/* Rebalance mode */}
          <select
            value={strategy.metadata.rebalanceMode}
            onChange={(e) => {
              const mode = e.target.value as 'daily' | 'threshold';
              updateMetadata({
                rebalanceMode: mode,
                rebalanceThreshold: mode === 'threshold' ? (strategy.metadata.rebalanceThreshold ?? 5) : undefined,
              });
            }}
            className="text-[10px] px-2 py-0.5 rounded bg-surface-800 border border-surface-600 text-surface-300 focus:outline-none cursor-pointer"
          >
            <option value="daily">Daily</option>
            <option value="threshold">Threshold</option>
          </select>

          {strategy.metadata.rebalanceMode === 'threshold' && (
            <div className="flex items-center gap-1">
              <input
                type="number"
                value={strategy.metadata.rebalanceThreshold ?? 5}
                onChange={(e) => updateMetadata({ rebalanceThreshold: Math.max(0.1, Math.min(100, Number(e.target.value))) })}
                min={0.1}
                max={100}
                step={0.5}
                className="w-14 text-[10px] px-1.5 py-0.5 rounded bg-surface-800 border border-surface-600 text-surface-300 focus:outline-none focus:border-primary-500/50"
              />
              <span className="text-[10px] text-surface-500">%</span>
            </div>
          )}
        </div>

        {/* Right: Actions — desktop: inline, mobile: hamburger menu */}
        <div className="flex items-center gap-1.5">
          {/* Mobile hamburger */}
          <button
            onClick={() => setShowMobileActions(!showMobileActions)}
            className="md:hidden text-xs p-1.5 rounded-md text-surface-400 hover:text-surface-200 hover:bg-surface-700/50 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={showMobileActions ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'} />
            </svg>
          </button>

          {/* Desktop actions — always visible on md+ */}
          <div className="hidden md:flex items-center gap-1.5">
            <button onClick={handleImport} className="text-xs px-2.5 py-1 rounded-md text-surface-400 hover:text-surface-200 hover:bg-surface-700/50 transition-colors">
              Import
            </button>
            <button onClick={handleExportJSON} className="text-xs px-2.5 py-1 rounded-md text-surface-400 hover:text-surface-200 hover:bg-surface-700/50 transition-colors">
              Export JSON
            </button>
            <button onClick={handleExportDSL} className="text-xs px-2.5 py-1 rounded-md text-surface-400 hover:text-surface-200 hover:bg-surface-700/50 transition-colors">
              Export DSL
            </button>

            {isAuthenticated && (
              <>
                <div className="w-px h-5 bg-surface-700 mx-1" />
                {source.type === 'new' && (
                  <>
                    <button
                      onClick={handleShareToCommunity}
                      disabled={isSaving}
                      className={clsx(
                        'text-xs px-3 py-1 rounded-md font-medium transition-colors',
                        isSaving
                          ? 'text-surface-500 bg-surface-700 cursor-not-allowed'
                          : 'text-primary-400 bg-primary-500/15 hover:bg-primary-500/25 border border-primary-500/30',
                      )}
                    >
                      {isSaving ? 'Saving...' : 'Share to Community'}
                    </button>
                    <button
                      onClick={handleBookmark}
                      disabled={isSaving}
                      className="text-xs px-2.5 py-1 rounded-md text-surface-400 hover:text-surface-200 hover:bg-surface-700/50 transition-colors disabled:opacity-50"
                      title="Save as private bookmark"
                    >
                      Bookmark
                    </button>
                  </>
                )}
                {source.type === 'community' && (
                  <button
                    onClick={handleBookmark}
                    disabled={isSaving}
                    className={clsx(
                      'text-xs px-3 py-1 rounded-md font-medium transition-colors',
                      isSaving
                        ? 'text-surface-500 bg-surface-700 cursor-not-allowed'
                        : 'text-primary-400 bg-primary-500/15 hover:bg-primary-500/25 border border-primary-500/30',
                    )}
                    title="Save a private copy to your bookmarks"
                  >
                    {isSaving ? 'Saving...' : 'Bookmark Copy'}
                  </button>
                )}
                {source.type === 'user' && (
                  <button
                    onClick={handleUpdate}
                    disabled={isSaving}
                    className={clsx(
                      'text-xs px-3 py-1 rounded-md font-medium transition-colors',
                      isSaving
                        ? 'text-surface-500 bg-surface-700 cursor-not-allowed'
                        : 'text-amber-300 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30',
                    )}
                  >
                    {isSaving ? 'Saving...' : 'Update Strategy'}
                  </button>
                )}
              </>
            )}

            <div className="w-px h-5 bg-surface-700 mx-1" />
            <button
              onClick={reset}
              className="text-xs px-2.5 py-1 rounded-md text-surface-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Mobile actions dropdown */}
      {showMobileActions && (
        <div className="md:hidden flex flex-wrap items-center gap-1.5 px-3 py-2 bg-surface-800/90 border-b border-surface-700 shrink-0">
          <button onClick={() => { handleImport(); setShowMobileActions(false); }} className="text-xs px-2.5 py-1.5 rounded-md text-surface-400 hover:text-surface-200 hover:bg-surface-700/50">
            Import
          </button>
          <button onClick={() => { handleExportJSON(); setShowMobileActions(false); }} className="text-xs px-2.5 py-1.5 rounded-md text-surface-400 hover:text-surface-200 hover:bg-surface-700/50">
            Export JSON
          </button>
          <button onClick={() => { handleExportDSL(); setShowMobileActions(false); }} className="text-xs px-2.5 py-1.5 rounded-md text-surface-400 hover:text-surface-200 hover:bg-surface-700/50">
            Export DSL
          </button>
          {isAuthenticated && (
            <>
              {source.type === 'new' && (
                <>
                  <button onClick={() => { handleShareToCommunity(); setShowMobileActions(false); }} disabled={isSaving} className="text-xs px-2.5 py-1.5 rounded-md text-primary-400 bg-primary-500/15 border border-primary-500/30">
                    {isSaving ? 'Saving...' : 'Share'}
                  </button>
                  <button onClick={() => { handleBookmark(); setShowMobileActions(false); }} disabled={isSaving} className="text-xs px-2.5 py-1.5 rounded-md text-surface-400 hover:text-surface-200 hover:bg-surface-700/50">
                    Bookmark
                  </button>
                </>
              )}
              {source.type === 'community' && (
                <button onClick={() => { handleBookmark(); setShowMobileActions(false); }} disabled={isSaving} className="text-xs px-2.5 py-1.5 rounded-md text-primary-400 bg-primary-500/15 border border-primary-500/30">
                  {isSaving ? 'Saving...' : 'Bookmark Copy'}
                </button>
              )}
              {source.type === 'user' && (
                <button onClick={() => { handleUpdate(); setShowMobileActions(false); }} disabled={isSaving} className="text-xs px-2.5 py-1.5 rounded-md text-amber-300 bg-amber-500/15 border border-amber-500/30">
                  {isSaving ? 'Saving...' : 'Update'}
                </button>
              )}
            </>
          )}
          <button onClick={() => { reset(); setShowMobileActions(false); }} className="text-xs px-2.5 py-1.5 rounded-md text-surface-500 hover:text-red-400 hover:bg-red-500/10">
            Reset
          </button>
        </div>
      )}

      {/* Save status banner */}
      {(saveSuccess || saveError) && (
        <div className={clsx(
          'px-4 py-1.5 text-xs font-medium text-center shrink-0 transition-all',
          saveSuccess ? 'bg-green-500/15 text-green-400 border-b border-green-500/30' : 'bg-red-500/15 text-red-400 border-b border-red-500/30',
        )}>
          {saveSuccess || saveError}
        </div>
      )}

      {/* Confirmation dialog for updating live-traded strategy */}
      {showConfirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-surface-800 border border-surface-600 rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-surface-100">Update Strategy?</h3>
                <p className="text-xs text-surface-400 mt-0.5">This will overwrite your existing strategy</p>
              </div>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mb-4">
              <p className="text-xs text-amber-300 leading-relaxed">
                <strong>Live Trading Warning:</strong> If this strategy is actively being traded,
                the changes will take effect on the next trading execution. The cached strategy
                will be invalidated immediately.
              </p>
            </div>
            <p className="text-xs text-surface-400 mb-4">
              Strategy: <span className="text-surface-200 font-medium">{source.strategyName || strategy.metadata.name}</span>
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="px-4 py-2 text-xs rounded-lg text-surface-400 hover:text-surface-200 bg-surface-700 hover:bg-surface-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmUpdate}
                className="px-4 py-2 text-xs rounded-lg font-medium text-amber-200 bg-amber-600 hover:bg-amber-500 transition-colors"
              >
                Yes, Update Strategy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Main Content ────────────────────────────────── */}
      {/* Mobile: view toggle between Tree and Panel */}
      <div className="md:hidden flex border-b border-surface-700 shrink-0">
        <button
          onClick={() => setMobileView('tree')}
          className={clsx(
            'flex-1 text-xs font-medium py-2.5 transition-colors',
            mobileView === 'tree'
              ? 'text-primary-400 border-b-2 border-primary-500 bg-surface-800/50'
              : 'text-surface-500 hover:text-surface-300',
          )}
        >
          Tree View
        </button>
        <button
          onClick={() => setMobileView('panel')}
          className={clsx(
            'flex-1 text-xs font-medium py-2.5 transition-colors',
            mobileView === 'panel'
              ? 'text-primary-400 border-b-2 border-primary-500 bg-surface-800/50'
              : 'text-surface-500 hover:text-surface-300',
          )}
        >
          Panel
        </button>
      </div>

      {/* Desktop: side-by-side | Mobile: one at a time */}
      <div className="flex-1 flex overflow-hidden">
        {/* Tree Canvas — hidden on mobile when panel is active */}
        <div className={clsx('flex-1 min-w-0', mobileView !== 'tree' && 'hidden md:flex')}>
          <TreeCanvas />
        </div>

        {/* Drag handle — desktop only */}
        <div
          className="hidden md:block w-1.5 shrink-0 cursor-col-resize bg-surface-700/50 hover:bg-primary-500/40 active:bg-primary-500/60 transition-colors"
          onPointerDown={handleResizePointerDown}
          onPointerMove={handleResizePointerMove}
          onPointerUp={handleResizePointerUp}
        />

        {/* Right Panel — full width on mobile, fixed width on desktop */}
        <div
          className={clsx(
            'flex flex-col bg-surface-800/80 md:border-l border-surface-700',
            mobileView !== 'panel' && 'hidden md:flex',
            mobileView === 'panel' ? 'w-full md:w-auto' : 'md:shrink-0',
          )}
          style={mobileView === 'panel' ? undefined : { width: rightPanelWidth }}
        >
          {/* Tab switcher */}
          <div className="flex border-b border-surface-700 shrink-0">
            <button
              onClick={() => setRightTab('properties')}
              className={clsx(
                'flex-1 text-xs font-medium py-2 transition-colors',
                rightTab === 'properties'
                  ? 'text-primary-400 border-b-2 border-primary-500'
                  : 'text-surface-500 hover:text-surface-300',
              )}
            >
              Properties
            </button>
            {!hideDsl && (
            <button
              onClick={() => setRightTab('code')}
              className={clsx(
                'flex-1 text-xs font-medium py-2 transition-colors',
                rightTab === 'code'
                  ? 'text-primary-400 border-b-2 border-primary-500'
                  : 'text-surface-500 hover:text-surface-300',
              )}
            >
              DSL Code
            </button>
            )}
            <button
              onClick={() => setRightTab('backtest')}
              className={clsx(
                'flex-1 text-xs font-medium py-2 transition-colors',
                rightTab === 'backtest'
                  ? 'text-primary-400 border-b-2 border-primary-500'
                  : 'text-surface-500 hover:text-surface-300',
              )}
            >
              Backtest
            </button>
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-hidden">
            {rightTab === 'properties' && <PropertiesPanel />}
            {rightTab === 'code' && !hideDsl && <CodePreview />}
            {rightTab === 'backtest' && (
              <div className="flex flex-col h-full">
                <BacktestPanel
                  onResult={handleBacktestResult}
                  onError={handleBacktestError}
                  isLoading={isBacktesting}
                  setIsLoading={setIsBacktesting}
                />
                {backtestError && (
                  <div className="px-3 pb-3">
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2.5 text-xs text-red-400">
                      {backtestError}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Backtest Results (bottom panel) ─────────────── */}
      {backtestResult && (
        <BacktestResults
          result={backtestResult}
          onClose={() => setBacktestResult(null)}
        />
      )}
    </div>
  );
}
